// Vendor
import type { Ref } from 'vue';
import { ref } from 'vue';

// Personal libs
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import { TokenStatusEnum } from '@shared/enum/token-status.enum';
import { TokenTypeEnum } from '@shared/enum/token-type.enum';
import { IDataTime } from '@shared/interface/cronjob.interface';
import { logger } from '@shared/lib/logger/logger-manager';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { IAuthResponse } from '@shared/interface/auth-response.interface';
import { IUser } from '@shared/interface/model.interface';

export abstract class SessionToken {
  /**
   * Properties
   */
  protected ttl?: number;
  protected retries?: number;
  protected tokenType?: TokenTypeEnum;
  protected fingerprint?: string;
  protected isRefreshing: boolean;

  /**
   * Reactive properties
   */
  public tokenStatus: Ref<TokenStatusEnum | undefined>;
  public remainingTime: Ref<number | undefined>; // Remaining time in seconds
  public remainingDataTime: Ref<IDataTime | undefined>; // Remaining time in data format

  /**
   * Constructor
   */
  public constructor(tokenType: TokenTypeEnum) {
    this.tokenType = tokenType;
    this.isRefreshing = false;

    // Init reactive properties
    this.tokenStatus = ref<TokenStatusEnum | undefined>(
      TokenStatusEnum.INACTIVE,
    );
    this.remainingTime = ref<number | undefined>(undefined);
    this.remainingDataTime = ref<IDataTime | undefined>(undefined);

    this.refreshTokenData();
  }

  /**
   * Setter / Getter
   */
  getRemainingDataTime(): IDataTime | undefined {
    return this.remainingDataTime.value;
  }

  getRemainingTime(): number | undefined {
    return this.remainingTime.value;
  }

  setTokenType(tokenType: TokenTypeEnum) {
    this.tokenType = tokenType;

    return this;
  }

  getTokenType(): TokenTypeEnum | undefined {
    return this.tokenType;
  }

  getTokenStatus(): TokenStatusEnum | undefined {
    return this.tokenStatus.value;
  }

  setTokenStatus(status?: TokenStatusEnum): this {
    this.tokenStatus.value = status;
    return this;
  }

  getTtl(): number | undefined {
    return this.ttl;
  }

  getRetries(): number | undefined {
    return this.retries;
  }

  getFingerprint(): string | undefined {
    return this.fingerprint;
  }
  
  /**
   * Abstract methods
   */
  abstract refreshTokenData(user?: IUser): Promise<void>;
  abstract refresh(params?: any): Promise<void | IApiResponse<IAuthResponse, string> | undefined>;
  abstract refreshStatus(): Promise<void>;
  abstract clearToken(): void;

  /**
   * Protected methods
   */
  protected log(message: string) {
    logger?.info(`[SessionManager] - ${message}`);
  }

  /**
   * Public methods
   */
  public clean() {
    this.remainingTime.value = undefined;
    this.remainingDataTime.value = undefined;
  }

  public async isValid(): Promise<boolean> {
    await this.refreshTokenData();
    await this.refreshStatus();

    return (
      this.tokenStatus.value === TokenStatusEnum.ACTIVE ||
      this.tokenStatus.value === TokenStatusEnum.END_OF_LIVE
    );
  }
}
